import React, { useEffect, useRef, useState } from "react";
import { Col, Row, Input, Modal, Table, Button, Space } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { triggerNotification } from "../../store/directActions";
import PageHeader from "../PageHeader";
import { onBarcodeScan } from "../../utils/barcodeEvents";
import { queryApi } from "../../store/actions/common";
import { PATH_STORE } from "../../config";

const { Search } = Input;

// Dummy API functions - Replace these with actual API endpoints
const fetchScanMetaApi = async (scanCode) => {
  return queryApi(`ledger/scan_meta/${scanCode}`, "GET");
};

const addInventoryApi = async (data) => {
  return queryApi(`slot/inventory`, "POST", data);
};

function ModalInventory({ showModal, toggleModal, onSuccess }) {
  const [localData, setLocalData] = useState([]);
  const [scanCode, setScanCode] = useState("");
  const scanInputRef = useRef(null);
  // Remembers the last code we sent so the scanner's trailing Enter (which
  // antd's Search turns into onSearch) doesn't re-submit the same barcode.
  const lastSubmittedRef = useRef({ code: "", at: 0 });
  const queryClient = useQueryClient();

  // Same key as PacksPage so react-query serves this from cache when available.
  const { data: storeConfig } = useQuery({
    queryKey: [PATH_STORE, "config", { type: "current" }],
    queryFn: () => queryApi(`store/config`, "GET"),
    staleTime: Infinity,
    gcTime: 0,
  });

  // Delay lets the modal finish its open animation before focusing.
  // cursor "all" selects any text already there, so the next scan overwrites
  // it instead of appending — an appended code would never match barcode_len.
  const focusScanInput = (cursor = "end") => {
    setTimeout(() => {
      scanInputRef.current?.focus({ cursor });
    }, 100);
  };

  // Reset on open so a reopened modal never shows the previous session's
  // leftover code or rows.
  useEffect(() => {
    if (showModal) {
      setScanCode("");
      setLocalData([]);
      lastSubmittedRef.current = { code: "", at: 0 };
      focusScanInput();
    }
  }, [showModal]);

  // Mutation for fetching scan metadata
  const { mutate: fetchScanMeta, isPending: isScanning } = useMutation({
    mutationFn: (code) => fetchScanMetaApi(code),
    onSuccess: (scan_meta) => {
      console.log("scanned meta", scan_meta);

      if (!scan_meta.is_inventory && !scan_meta.is_active) {
        if (!localData.some((item) => item.pack === scan_meta.pack)) {
          if (scan_meta.amount) {
            setLocalData([scan_meta, ...localData]);
            setScanCode("");
          }
        } else {
          triggerNotification({
            title: "Error",
            description: "You have already added this pack no",
            type: "error",
          });
        }
      } else {
        console.log("show error not in inventory");
        if (scan_meta.is_inventory) {
          triggerNotification({
            title: "Error",
            description: "This pack is already in inventory",
            type: "error",
          });
        }
        if (scan_meta.is_active) {
          triggerNotification({
            title: "Error",
            description: "This pack is already active",
            type: "error",
          });
        }
      }
      // Keep the scan field ready for the next code. A rejected code stays
      // visible but selected, so rescanning replaces it.
      focusScanInput("all");
    },
    onError: (error) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to scan code",
        type: "error",
      });
      focusScanInput("all");
    },
  });

  // Mutation for adding inventory
  const { mutate: addInventory, isPending: isAdding } = useMutation({
    mutationFn: (data) => addInventoryApi(data),
    onSuccess: () => {
      setLocalData([]);
      toggleModal(false);
      queryClient.invalidateQueries({
        queryKey: ["slot/inventory"],
      });
      if (onSuccess) {
        onSuccess();
      }
      triggerNotification({
        title: "Success",
        description: "Inventory added successfully",
        type: "success",
      });
    },
    onError: (error) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to add inventory",
        type: "error",
      });
    },
  });

  const getSerchedData = (data) => {
    const code = (data ?? "").trim();
    if (!code) return;

    const { code: lastCode, at } = lastSubmittedRef.current;
    if (code === lastCode && Date.now() - at < 1000) return;

    lastSubmittedRef.current = { code, at: Date.now() };
    fetchScanMeta(code);
  };

  const onFormSubmit = () => {
    console.log("form data", localData);

    addInventory(
      localData.map((item) => {
        return { scan_code: item.scan_code };
      }),
    );
  };

  const inventoryColumns = [
    {
      title: "#",
      dataIndex: "game_number",
      key: "game_number",
      render: (item, record, index) => <div>{index + 1}</div>,
      width: "48px",
    },
    {
      title: "Game",
      dataIndex: "game_number",
      key: "game_number",
      render: (item, record, index) => (
        <div>
          {record.name} - {record.game_number}
        </div>
      ),
      width: "160px",
      sorter: (a, b) => a.game_number - b.game_number,
      defaultSortOrder: "ascend",
    },
    {
      title: <div style={{ minWidth: "max-content" }}>Pack #</div>,
      dataIndex: "pack",
      key: "pack",
      sorter: (a, b) => a.pack - b.pack,
      defaultSortOrder: "ascend",
      render: (item, record, index) => (
        <div>
          {record.pack}
          {/* <br></br>
          {record.pack_size} */}
        </div>
      ),
      width: 100,
    },
    {
      title: "Value",
      dataIndex: "amount",
      key: "amount",
      render: (item, record, index) => (
        <div>
          {record.currency}
          {record.amount}
        </div>
      ),
    },
    {
      title: <div style={{ minWidth: "max-content" }}>Total Value</div>,
      dataIndex: "totalPack",
      key: "totalPack",
      render: (item, record, index) => (
        <div>
          {record.currency}
          {record.amount * record.pack_size}
        </div>
      ),
      width: 150,
    },
    {
      title: "Action",
      key: "action",
      render: (item, record, index) => (
        <Space size="middle">
          <DeleteOutlined
            style={{ fontSize: 22, color: "red" }}
            onClick={() => {
              setLocalData(
                localData.filter((item, itemIndex) => itemIndex !== index),
              );
            }}
          />
        </Space>
      ),
    },
  ];

  useEffect(() => {
    return onBarcodeScan(async (barcode) => {
      console.log("barcode scanned", barcode, showModal);
      if (showModal) getSerchedData(barcode);
    });
  }, [showModal]);

  return (
    <Modal
      width="80%"
      style={{ height: "80vh" }}
      maskClosable={false}
      title="Add Inventory"
      open={showModal}
      onOk={() => onFormSubmit()}
      onCancel={() => {
        setLocalData([]);
        toggleModal(false);
      }}
      okButtonProps={{
        disabled: localData?.length === 0,
        loading: isAdding,
      }}
      cancelButtonProps={{ disabled: isAdding }}
    >
      <Row gutter={16}>
        <Col span={24}>
          <Search
            ref={scanInputRef}
            placeholder="enter ticket code, or scan"
            enterButton="Add"
            size="medium"
            allowClear
            value={scanCode}
            onChange={(e) => {
              const value = e.target.value;
              setScanCode(value);
              // A hardware scanner types straight into this field when it has
              // focus, so BarcodeReader never sees the keypresses. Submit as
              // soon as the value reaches the configured barcode length.
              if (
                storeConfig?.barcode_len &&
                value?.length === storeConfig.barcode_len
              )
                getSerchedData(value);
            }}
            onSearch={getSerchedData}
            loading={isScanning}
          />
        </Col>

        <Col span={24}>
          <PageHeader
            style={{ padding: 12 }}
            ghost={true}
            title={`${"$"}${localData.reduce(
              (value, item) => value + item.amount * item.pack_size,
              0,
            )}`}
            subTitle={`Total pack : ${localData.length}`}
            extra={[
              <Button
                key="3"
                onClick={() => {
                  setLocalData([]);
                }}
                disabled={isAdding}
              >
                Clear
              </Button>,
            ]}
            wrapperClasses="mt-4"
          />
        </Col>
        <Col span={24}>
          <div style={{ overflowY: "auto" }}>
            <Table
              dataSource={localData}
              scroll={{ y: 280 }}
              bordered
              columns={inventoryColumns}
              pagination={false}
            />
          </div>
        </Col>
      </Row>
    </Modal>
  );
}

export default ModalInventory;
